/**
 * Lógica pura de la pantalla del minero. Vive aquí, fuera del componente, para poder
 * ejecutarla en `miner.check.ts` sin montar React.
 */

/**
 * Cada cuánto repreguntar el estado. Solo con el minero encendido: apagado no cambia nada
 * y sondear sería gastar peticiones a cambio de nada.
 *
 * 15 s porque una pasada tarda entre segundos y un par de minutos, y se quiere ver el
 * gasto y las capturas moverse sin recargar.
 *
 * OJO con la firma: en react-query v3 el primer argumento son LOS DATOS, no la query.
 * Antes esto leía `query.state.data.enabled` sobre el objeto de datos, siempre undefined,
 * así que devolvía `false` siempre y el sondeo no llegó a ejecutarse nunca. El parámetro
 * va como `unknown` porque esta configuración la comparten las tres consultas de la
 * pantalla y tiparlo concreto obligaba a TypeScript a deducir ese tipo para todas.
 */
export function pollWhileOn(data?: unknown): number | false {
  return (data as { enabled?: boolean } | undefined)?.enabled ? 15_000 : false
}

/**
 * "hace 3 minutos". Los plurales y el formato los pone Intl, no una tabla propia.
 *
 * Los saltos de unidad se eligen por el uso real: por debajo del minuto el número exacto
 * de segundos no aporta ("hace un momento" dice lo mismo), y a partir de una hora los
 * minutos sobran.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60_000)
  if (!Number.isFinite(minutes)) return ''
  if (minutes < 1) return 'hace un momento'

  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
  if (minutes < 60) return rtf.format(-minutes, 'minute')
  if (minutes < 1440) return rtf.format(-Math.round(minutes / 60), 'hour')
  return rtf.format(-Math.round(minutes / 1440), 'day')
}

/**
 * Capturas en voz alta: lo que se guardó y lo que queda en pie tras el clasificador.
 *
 * El minero insertaba y cantaba el número ahí mismo, pero el clasificador descarta lo que
 * queda fuera de objetivo en los segundos siguientes —medido, 7 de cada 10—. De ahí que
 * dijera "12 capturas" y el contador de la pestaña de capturas no se moviera.
 *
 * `kept` ausente es una pasada anterior a este contador: no se puede reconstruir, y poner
 * un 0 o un "→ undefined" sería peor que enseñar solo lo que sí se sabe.
 */
export function describirCapturas(captures?: number, kept?: number): string {
  const n = captures || 0
  if (kept === undefined || kept === null || kept === n) return `${n} capturas`
  return `${n} capturas → ${kept} se quedan`
}

/**
 * Marca que identifica a la última pasada Y a su estado.
 *
 * Lleva `finishedAt` además de `startedAt` porque la pasada se registra al EMPEZAR: con
 * solo la fecha de inicio, el historial se quedaba con la fila a medias —cero capturas,
 * cero créditos— hasta que alguien volviera a entrar a la pantalla.
 */
export function marcaDePasada(
  lastRun?: { startedAt?: string; finishedAt?: string } | null
): string | null {
  if (!lastRun?.startedAt) return null
  return `${lastRun.startedAt}|${lastRun.finishedAt ?? ''}`
}

/**
 * ¿Hay que volver a pedir el historial y las semillas?
 *
 * Esas dos consultas no sondean: tres repreguntando en paralelo devolvían 429 a toda la
 * aplicación. El estado sí sondea, así que hace de aviso. Solo se repiden cuando ya
 * conocíamos una marca y la nueva es distinta: con `pedida` a null estamos en el primer
 * render con datos y la lista acaba de pedirse sola al montar, así que pedirla otra vez
 * sería duplicar la petición que se intenta ahorrar.
 */
export function repedirHistorial(marca: string | null, pedida: string | null): boolean {
  return marca !== null && pedida !== null && marca !== pedida
}
